import { useEffect, useRef, useState } from 'react';
import FontSelection from '../screens/fontSelection/FontSelection';
import ContrastSelection from './contrastSelection/ContrastSelection';
import MicrophoneSelectionMain from './microphoneSelection/MicrophoneSelectionMain';
import SummaryScreen from './summaryScreen/SummaryScreen';
import { TextIcon, ColourIcon, SoundIcon, MicIcon, VideoIcon } from '../components/styledIcons';
import { Stepper, StepProps, Step } from '../components/Stepper';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../redux/store';
import { pushFaroEvent, setFaroSession } from '../faroSession';
import SoundSelectionMain from './soundSelection/SoundSelectionMain';
import { useOnnx } from '../hooks/useAudioRecorder/subHooks/useOnnx';
import { fetchTestRules } from '../api/authApis';
import { ISpeakCheckRules } from './microphoneSelection/SpeakCheckScreen';
import { useLoadTestResource } from '../hooks/useTestLogic/subHooks/useLoadTestResource';
import { setContentData } from '../redux/appConfigSlice';

const PersonalizationScreen = () => {
    const dispatch = useDispatch();
    const {
        appFontSize,
        pageObject: { pageIndex },
        role
    } = useSelector((state: RootState) => state.appConfigSlice);
    const [isVideoPlayed, setIsVideoPlayed] = useState<boolean>(false);
    const errorCode = useRef<string>('');
    const rules = useRef<ISpeakCheckRules>({
        silenceBeforeRetry: 0,
        maxSpeechTime: 0,
        silenceRequiredForComplete: 0,
        samplePhrase: '',
    });
    const { testTaker: testTakerDetails } = useSelector((state: RootState) => state.appConfigSlice);

    const resource = useLoadTestResource({ fetchOnLoad: false });
    const { workerRef, ready, initWorker, resetSileroStates } = useOnnx();

    useEffect(() => {
        initWorker();
        return resetSileroStates();
    }, [initWorker]);

    useEffect(() => {
        const getRules = async () => {
             const response: { preCheck: ISpeakCheckRules; errorCode?: string, message?: string  } = await fetchTestRules(testTakerDetails);
             if (response.errorCode) {
                errorCode.current = response.errorCode;
             } else {
                rules.current = response.preCheck;
             }
        } 

        getRules();
    }, []);

    useEffect(() => {
        if( role !== 'ASSESSMENT') {
            resource
            .fetchContents()
            .then((res) => {
                dispatch(setContentData(res.data));
            })
            .catch((e) => {
                console.error('Error in initial content fetch: ', e);
            });
        }
    }, []);

    useEffect(() => {
        setFaroSession({ stage: 'precheck' });
        pushFaroEvent('precheck_started');
    }, []);

    let path = [
        {
            sectionName: 'Choose your font size',
            index: 0,
            Component: <FontSelection />,
        },
        {
            sectionName: 'Choose your colour mode',
            index: 1,
            Component: <ContrastSelection />,
        },
        {
            sectionName: 'Check the sound',
            index: 2,
            Component: <SoundSelectionMain />,
        },
        {
            sectionName: 'Choose the microphone',
            index: 3,
            Component: <MicrophoneSelectionMain workerRef={workerRef} ready={ready} rules={rules.current} />,
        },
        {
            sectionName: `Watch the video`,
            index: 4,
            Component: <SummaryScreen isVideoPlayed={isVideoPlayed} setIsVideoPlayed={setIsVideoPlayed} />,
        },
    ];

    let currentComponent = path[pageIndex];

    const getSteps = (): StepProps[] => {
        const steps = [
            { label: '1. Choose text size', icon: <TextIcon /> },
            { label: '2. Choose colour', icon: <ColourIcon /> },
            { label: '3. Check sound', icon: <SoundIcon /> },
            { label: '4. Check microphone', icon: <MicIcon /> }
        ];
        if (role !== 'ASSESSMENT') {
            steps.push({ label: '5. Watch video', icon: <VideoIcon /> });
        }
        return steps;
    };

    const getStepperWidth = () => {
        // values in rem units
        switch (appFontSize) {
            case 'sm':
                return 100;
            case 'md':
                return 110;
            case 'lg':
                return 120;
        }
    };

    return (
        <>
            <Stepper currentActive={currentComponent.index} width={getStepperWidth()}>
                {getSteps().map((step: StepProps, index) => {
                    return <Step {...step} key={'step' + index} />;
                })}
            </Stepper>
            {currentComponent.Component}
        </>
    );
};

export default PersonalizationScreen;
